<script setup>
import { Switch as HeadlessUiSwitch } from "@headlessui/vue";
import { CheckIcon, MinusIcon, PlusIcon } from "@heroicons/vue/20/solid";
import { computed, useSlots } from "vue";
import { twMerge } from "tailwind-merge";
import Tooltip from "@Components/Tooltip.vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
    modelValue: {},
    title: {
        type: String,
        required: false,
    },
    description: {
        type: String,
        required: false,
    },
    icon: {
        type: [Object, Function],
        required: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    z: {
        type: Number,
        default: 0,
    },
    size: {
        type: String,
        default: "normal",
    },
    tooltip: String,
    class: String,
});

const emit = defineEmits(["update:modelValue"]);

const { t: __ } = useI18n();
const slots = useSlots();

const _disabled = computed(() => {
    if (props.disabled) {
        return true;
    }

    return false;
});

const _tooltip = computed(() => {
    return props.tooltip;
});

const switchClasses = computed(() => {
    let classes = [
        !_disabled.value ? "focus:ring-gray-950 focus:ring-2 focus:ring-inset" : "pointer-events-none",
        "group cursor-pointer relative h-full flex space-x-3 rounded-lg border-0 ring-1 ring-inset",
        "bg-white ring-gray-300",
        props.size === "small" ? "px-3 py-2.5" : "p-4",
    ];

    switch (props.z) {
        case 3:
            classes.push([!_disabled.value ? "dark:focus:ring-gray-400" : "", "dark:bg-gray-600 dark:ring-gray-500"]);
            break;
        case 2:
            classes.push([!_disabled.value ? "dark:focus:ring-gray-500" : "", "dark:bg-gray-700 dark:ring-gray-600"]);
            break;
        case 1:
            classes.push([!_disabled.value ? "dark:focus:ring-gray-600" : "", "dark:bg-gray-800 dark:ring-gray-700"]);
            break;
        default:
            classes.push([!_disabled.value ? "dark:focus:ring-gray-700" : "", "dark:bg-gray-900 dark:ring-gray-800"]);
            break;
    }

    return twMerge(classes, props.class);
});

const checkedSwitchClasses = computed(() => {
    let classes = ["ring-gray-950"];

    switch (props.z) {
        case 3:
            classes.push("dark:ring-gray-400");
            break;
        case 2:
            classes.push("dark:ring-gray-500");
            break;
        case 1:
            classes.push("dark:ring-gray-600");
            break;
        default:
            classes.push("dark:ring-gray-700");
            break;
    }

    return twMerge(switchClasses.value, classes, props.class);
});

const iconClasses = computed(() => {
    let classes = ["absolute items-center justify-center right-0 top-0 p-0.5 translate-x-1/2 -translate-y-1/2 rounded-full", "bg-gray-950"];

    switch (props.z) {
        case 3:
            classes.push("dark:bg-gray-400");
            break;
        case 2:
            classes.push("dark:bg-gray-500");
            break;
        case 1:
            classes.push("dark:bg-gray-600");
            break;
        default:
            classes.push("dark:bg-gray-700");
            break;
    }

    return twMerge(classes);
});
</script>

<template>
    <div class="h-full">
        <component :is="_tooltip ? Tooltip : 'div'" :content="_tooltip" class="h-full">
            <HeadlessUiSwitch
                as="template"
                :modelValue="modelValue"
                @update:modelValue="(value) => (!_disabled ? emit('update:modelValue', value) : undefined)"
                v-slot="{ checked }">
                <div :class="checked ? checkedSwitchClasses : switchClasses">
                    <slot>
                        <slot name="icon">
                            <Component v-if="icon" :is="icon" :class="[size === 'small' ? 'size-4' : 'size-6', 'flex-none text-gray-900 dark:text-white']" />
                        </slot>

                        <div :class="[!description && !!!slots.description ? 'flex flex-col justify-center' : '', 'flex-1']">
                            <div v-if="title || !!slots.title" :class="[size === 'small' ? 'leading-4' : 'leading-6', 'text-sm text-gray-900 dark:text-white']">
                                <slot name="title" :checked="checked"> {{ title }}</slot>
                            </div>
                            <div
                                v-if="description || !!slots.description"
                                :class="[title || !!slots.title ? 'mt-1' : '', 'flex items-center text-sm text-gray-600 dark:text-gray-400']">
                                <slot name="description" :checked="checked"> {{ description }}</slot>
                            </div>
                        </div>
                    </slot>
                    <div :class="[iconClasses, checked ? 'flex' : 'hidden group-hover:flex']">
                        <MinusIcon v-if="checked" :class="size === 'small' ? 'w-3 h-3' : 'w-4 h-4'" class="hidden text-white group-hover:block" />
                        <PlusIcon v-else :class="size === 'small' ? 'w-3 h-3' : 'w-4 h-4'" class="hidden text-white group-hover:block" />
                        <CheckIcon :class="size === 'small' ? 'w-3 h-3' : 'w-4 h-4'" class="block text-white group-hover:hidden" />
                    </div>
                </div>
            </HeadlessUiSwitch>
        </component>
    </div>
</template>
